import React, { Component } from 'react';
import { ID, l, withCodes2 } from 'app/blocks/common/codes';
import ContentUtils from 'app/blocks/common/content-utils';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import Alerts from 'app/blocks/common/spinner';
import { compose, cloneDeepSimple } from 'app/blocks/common/utils';
import withPreloading from 'app/blocks/common/with-preloading-hoc';
import withConfirmLeaving from 'app/blocks/common/withConfirmLeaving';
import Layout from 'app/blocks/Layout/Layout';
import { getAlerts, getProfileUrl, saveAlerts } from 'app/blocks/middleware/profile';

import PageLayout from 'app/blocks/page/PageLayout';
import AlertsPanel from './alerts-panel';

import './alerts.scss';

type AlertsPageProps = {
    profileUrl: string;
    setChangesChecker: (checkerFn: () => boolean) => void;
};

type AlertsType = {
    alerts?: {
        alertId: string;
        alertName: string;
        email: boolean;
        onScreen: boolean;
    }[];
    emails?: string[];
    notificationEmail?: string;
};

type AlertsPageState = {
    alerts?: AlertsType;
    savedAlerts?: AlertsType;
    isLoading: boolean;
    isSaveEnabled: boolean;
    errorMsg: string;
};

class AlertsPage extends Component<AlertsPageProps, AlertsPageState> {
    state = {
        alerts: undefined,
        errorMsg: '',
        isLoading: false,
        isSaveEnabled: false,
        savedAlerts: undefined,
    };

    async componentDidMount() {
        try {
            Alerts.renderSpinner();

            const alerts = await getAlerts();
            this.setState({
                alerts,
                isLoading: false,
                savedAlerts: cloneDeepSimple(alerts),
            });
        } catch (error) {
            showDialog.error(error, {
                message: error.message || (await ContentUtils.getServerErrorMessage(ID.PROFILE_ACCOUNT, error)),
            });
            this.setState({
                errorMsg: l('PROFILE_ACCOUNT.errorMessages.UNABLE_TO_LOAD_CONTENT'),
                isLoading: false,
            });
        }
        Alerts.removeSpinner();
    }

    hasAlertsChanged = (newAlerts: AlertsType): boolean => {
        const { savedAlerts } = this.state;
        return newAlerts.alerts?.some(
            ({ alertId, email }) => savedAlerts?.alerts.find(({ alertId: id }) => alertId === id)?.email !== email,
        );
    };

    toggleChangesChecker = (hasChanged: boolean) => {
        this.props.setChangesChecker(() => hasChanged);
    };

    toggleAlert = (alertId, checked) => {
        const alerts = this.state?.alerts;
        const newAlerts = alerts?.alerts.map(alert =>
            alert.alertId === alertId ? { ...alert, email: checked } : alert,
        );
        const alertsHasChanged = this.hasAlertsChanged({ ...alerts, alerts: newAlerts });
        this.toggleChangesChecker(alertsHasChanged);
        this.setState({
            alerts: { ...alerts, alerts: newAlerts },
            isSaveEnabled: alertsHasChanged,
        });
    };

    cancelAlerts = () => {};

    saveAlerts = async () => {
        const { alerts } = this.state;

        try {
            Alerts.renderSpinner();
            this.setState({ isLoading: true });
            const response = await saveAlerts({
                alerts: alerts.alerts,
                notificationEmail: alerts.notificationEmail,
            });
            if (response === true) {
                this.toggleChangesChecker(false);
                this.setState({
                    isSaveEnabled: false,
                    savedAlerts: cloneDeepSimple(alerts),
                });
                showDialog.success({
                    message: l('PROFILE_ACCOUNT.confirmationMessages.PROFILE_ACCNT_SAVE_ALERTS_INFO_POST_CONF_TEXT'),
                    onClose: this.cancelAlerts,
                });
            } else if ((response as Error).message) {
                showDialog.error(response as Error, {
                    message:
                        (response as Error).message ||
                        (await ContentUtils.getServerErrorMessage(ID.PROFILE_ACCOUNT, response as Error)),
                });
            }
        } catch (error) {
            showDialog.error(error);
        }
        this.setState({ isLoading: false });
        Alerts.removeSpinner();
    };

    render() {
        const { alerts, errorMsg, isLoading, isSaveEnabled } = this.state;
        const { profileUrl } = this.props;
        return (
            <main className="AlertsPage" id="as-main-content-7f3d9e2a">
                <PageLayout
                    seleniumid="alerts-pageTitle"
                    title={l('PROFILE_ACCOUNT.uiLabelMessages.PROFILE_ACCNT_ALERTS_LABEL')}
                >
                    {errorMsg || isLoading ? (
                        <Layout className="m-top_mini" errorMsg={errorMsg} isLoading={isLoading} />
                    ) : (
                        <AlertsPanel
                            alerts={alerts?.alerts}
                            isSaveEnabled={isSaveEnabled}
                            myAccountHref={profileUrl}
                            notificationEmail={alerts?.notificationEmail}
                            saveAlerts={this.saveAlerts}
                            toggleAlert={this.toggleAlert}
                        />
                    )}
                </PageLayout>
            </main>
        );
    }
}

export default compose(
    withConfirmLeaving,
    withCodes2(ID.PROFILE_ACCOUNT),
)(
    withPreloading(AlertsPage, {
        dataMapping: (d: { url: any }) => ({ profileUrl: d?.url }),
        loadDataFunction: getProfileUrl,
    }),
);
